import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import { ThemeProvider } from './context/ThemeContext';
import { WorkspaceProvider } from './context/WorkspaceContext';
import { AuthProvider } from './context/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import RouteTracker from './components/RouteTracker';

// Authenticated Pages
import Dashboard from './pages/Dashboard';
import Matters from './pages/Matters';
import Research from './pages/Research';
import Documents from './pages/Documents';
import Courts from './pages/Courts';
import Contracts from './pages/Contracts';
import Clients from './pages/Clients';
import Billing from './pages/Billing';
import Avatar from './pages/Avatar';
import Calendar from './pages/Calendar';
import Analytics from './pages/Analytics';
import Settings from './pages/Settings';
import Login from './pages/Login';

// Admin Pages
import {
  AdminDashboard, UserManagement, PageAccessManager,
  AdminActivityLog, SecurityEvents, VisitorActivity,
  UserActivity, SessionTimeline, SecurityDashboard
} from './pages/admin';

// Public Pages
import Home from './pages/public/Home';
import About from './pages/public/About';
import Products from './pages/public/Products';
import Lios from './pages/public/Lios';
import Privacy from './pages/public/Privacy';
import Terms from './pages/public/Terms';
import Help from './pages/public/Help';
import Contact from './pages/public/Contact';
import AccountDataDeletion from './pages/public/AccountDataDeletion';
import NotFound from './pages/public/NotFound';

// Legacy compatibility: redirect old top-level app paths to /app/* (preserves bookmarks)
const LegacyRedirect = () => {
  const location = useLocation();
  return <Navigate to={`/app${location.pathname}${location.search}`} replace />;
};

const protectedPage = (pageId, element) => (
  <ProtectedRoute pageId={pageId}>{element}</ProtectedRoute>
);
const adminPage = (pageId, element) => (
  <ProtectedRoute requireAdmin pageId={pageId}>{element}</ProtectedRoute>
);

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <WorkspaceProvider>
          <Router>
            <Toaster position="top-right" richColors closeButton />
            <RouteTracker />
            <Routes>
              {/* ================= PUBLIC EXPERIENCE ================= */}
              <Route path="/" element={<Home />} />
              <Route path="/about" element={<About />} />
              <Route path="/products" element={<Products />} />
              <Route path="/products/lios" element={<Lios />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/help" element={<Help />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/account-data-deletion" element={<AccountDataDeletion />} />

              {/* ================= AUTH GATEWAY ================= */}
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Login />} />

              {/* ================= AUTHENTICATED PLATFORM (/app/*) ================= */}
              <Route path="/app" element={protectedPage('dashboard', <Dashboard />)} />
              <Route path="/app/matters" element={protectedPage('matters', <Matters />)} />
              <Route path="/app/matters/:id" element={protectedPage('matters', <Matters />)} />
              <Route path="/app/research" element={protectedPage('research', <Research />)} />
              <Route path="/app/documents" element={protectedPage('documents', <Documents />)} />
              <Route path="/app/courts" element={protectedPage('courts', <Courts />)} />
              <Route path="/app/contracts" element={protectedPage('contracts', <Contracts />)} />
              <Route path="/app/clients" element={protectedPage('clients', <Clients />)} />
              <Route path="/app/clients/:id" element={protectedPage('clients', <Clients />)} />
              <Route path="/app/billing" element={protectedPage('billing', <Billing />)} />
              <Route path="/app/avatar" element={protectedPage('avatar', <Avatar />)} />
              <Route path="/app/calendar" element={protectedPage('calendar', <Calendar />)} />
              <Route path="/app/analytics" element={protectedPage('analytics', <Analytics />)} />
              <Route path="/app/settings" element={protectedPage('settings', <Settings />)} />

              {/* ================= LEGACY COMPATIBILITY REDIRECTS ================= */}
              <Route path="/matters/*" element={<LegacyRedirect />} />
              <Route path="/research/*" element={<LegacyRedirect />} />
              <Route path="/documents/*" element={<LegacyRedirect />} />
              <Route path="/courts/*" element={<LegacyRedirect />} />
              <Route path="/contracts/*" element={<LegacyRedirect />} />
              <Route path="/clients/*" element={<LegacyRedirect />} />
              <Route path="/billing/*" element={<LegacyRedirect />} />
              <Route path="/avatar/*" element={<LegacyRedirect />} />
              <Route path="/calendar/*" element={<LegacyRedirect />} />
              <Route path="/analytics/*" element={<LegacyRedirect />} />
              <Route path="/settings/*" element={<LegacyRedirect />} />

              {/* ================= ADMINISTRATION (unchanged, stays under /admin) ================= */}
              <Route path="/admin" element={adminPage('admin_dashboard', <AdminDashboard />)} />
              <Route path="/admin/users" element={adminPage('admin_users', <UserManagement />)} />
              <Route path="/admin/access" element={adminPage('admin_access', <PageAccessManager />)} />
              <Route path="/admin/activity" element={adminPage('admin_activity', <AdminActivityLog />)} />
              <Route path="/admin/security-events" element={adminPage('admin_security_events', <SecurityEvents />)} />
              <Route path="/admin/visitors" element={adminPage('admin_visitors', <VisitorActivity />)} />
              <Route path="/admin/user-activity" element={adminPage('admin_user_activity', <UserActivity />)} />
              <Route path="/admin/sessions" element={adminPage('admin_sessions', <SessionTimeline />)} />
              <Route path="/admin/security" element={adminPage('admin_security_dashboard', <SecurityDashboard />)} />

              {/* ================= CATCH-ALL ================= */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Router>
        </WorkspaceProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
